/**
 * Security validation utilities
 */

const ALLOWED_LOGO_DOMAINS = [
  'blob.core.windows.net', // Azure Blob Storage
  'cloudfront.net',        // AWS CloudFront
  'storage.googleapis.com', // Google Cloud Storage
];

/**
 * Validate and sanitize logo URL to prevent SSRF and XSS
 */
export function sanitizeLogoUrl(url: string): string | null {
  if (!url || url.trim() === '') {
    return null;
  }

  try {
    const parsedUrl = new URL(url);

    // Only allow HTTPS (no http, no data:, no javascript:)
    if (parsedUrl.protocol !== 'https:') {
      console.warn('Logo URL must use HTTPS protocol:', url);
      return null;
    }

    // Whitelist allowed domains
    const isAllowedDomain = ALLOWED_LOGO_DOMAINS.some(domain =>
      parsedUrl.hostname.endsWith(domain)
    );

    if (!isAllowedDomain) {
      console.warn('Logo URL domain not allowed:', parsedUrl.hostname);
      return null;
    }

    return url;
  } catch (error) {
    console.error('Invalid logo URL:', error);
    return null;
  }
}

/**
 * Sanitize user input to prevent injection attacks
 */
export function sanitizeInput(input: string): string {
  // Trim whitespace
  let sanitized = input.trim();

  // Remove null bytes
  sanitized = sanitized.replace(/\0/g, '');

  return sanitized;
}

/**
 * Sanitize email address
 */
export function sanitizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Sanitize phone number (keep only digits and common separators)
 */
export function sanitizePhone(phone: string): string {
  return phone.replace(/[^\d\s\-()+]/g, '');
}

/**
 * Email validation regex - RFC 5322 compliant (simplified)
 */
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Phone number validation regex - supports various formats:
 * - E.164: +12025551234
 * - US format: (202) 555-1234, 202-555-1234, 202.555.1234
 * - With spaces: 202 555 1234
 * - International: +1 202 555 1234
 */
const PHONE_REGEX = /^(\+?1[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/;

/**
 * E.164 phone format regex (strict international format)
 */
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * Validate email address
 * @param email - Email address to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string | null | undefined): boolean {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  return EMAIL_REGEX.test(trimmed);
}

/**
 * Validate phone number (flexible format)
 * @param phone - Phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhone(phone: string | null | undefined): boolean {
  if (!phone || typeof phone !== 'string') return false;
  const trimmed = phone.trim();
  if (trimmed.length === 0) return false;
  // Remove all non-digit characters except + for E.164 check
  const digitsOnly = trimmed.replace(/\D/g, '');
  // Must have at least 10 digits (US numbers) or valid format
  if (digitsOnly.length < 10 || digitsOnly.length > 15) return false;
  return PHONE_REGEX.test(trimmed) || E164_REGEX.test(trimmed);
}

/**
 * Validate E.164 phone format (strict)
 * @param phone - Phone number to validate
 * @returns true if valid E.164 format, false otherwise
 */
export function isValidE164Phone(phone: string | null | undefined): boolean {
  if (!phone || typeof phone !== 'string') return false;
  return E164_REGEX.test(phone.trim());
}

/**
 * Get email validation error message
 * @param email - Email to validate
 * @param fieldName - Name of the field for error message
 * @param required - Whether the field is required
 * @returns Error message or null if valid
 */
export function getEmailError(
  email: string | null | undefined,
  fieldName = 'Email',
  required = false
): string | null {
  const trimmed = email?.trim() || '';

  if (!trimmed) {
    return required ? `${fieldName} is required` : null;
  }

  if (trimmed.length > 254) {
    return `${fieldName} must not exceed 254 characters`;
  }

  if (!isValidEmail(trimmed)) {
    return `Please enter a valid email address`;
  }

  return null;
}

/**
 * Get phone validation error message
 * @param phone - Phone to validate
 * @param fieldName - Name of the field for error message
 * @param required - Whether the field is required
 * @returns Error message or null if valid
 */
export function getPhoneError(
  phone: string | null | undefined,
  fieldName = 'Phone',
  required = false
): string | null {
  const trimmed = phone?.trim() || '';

  if (!trimmed) {
    return required ? `${fieldName} is required` : null;
  }

  if (!isValidPhone(trimmed)) {
    return `Please enter a valid phone number (e.g., (555) 123-4567 or +15551234567)`;
  }

  return null;
}

/**
 * Format phone number as user types for display purposes.
 * Formats to US standard: (XXX) XXX-XXXX
 * @param value - Raw input value
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(value: string): string {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Limit to 10 digits (US format)
  const truncated = digits.slice(0, 10);

  // Format based on length
  if (truncated.length === 0) return '';
  if (truncated.length <= 3) return `(${truncated}`;
  if (truncated.length <= 6) return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
  return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
}

/**
 * Strip phone number to digits only for database storage.
 * Keeps phone numbers clean and consistent in the database.
 * @param phone - Phone number to strip
 * @returns Digits only, or empty string if no digits
 */
export function stripPhoneToDigits(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') return '';
  return phone.replace(/\D/g, '');
}

/**
 * Normalize phone number to E.164 format for API submission
 * @param phone - Phone number to normalize
 * @param defaultCountryCode - Default country code (default: '1' for US)
 * @returns E.164 formatted phone or null if invalid
 */
export function normalizeToE164(
  phone: string | null | undefined,
  defaultCountryCode = '1'
): string | null {
  if (!phone || typeof phone !== 'string') return null;

  // Remove all non-digit characters except leading +
  let cleaned = phone.trim();
  const hasPlus = cleaned.startsWith('+');
  cleaned = cleaned.replace(/\D/g, '');

  if (cleaned.length === 0) return null;

  // If it already has a country code (11+ digits starting with 1 for US)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // If it's a 10-digit number, add the default country code
  if (cleaned.length === 10) {
    return `+${defaultCountryCode}${cleaned}`;
  }

  // If it had a plus and valid length, return as-is
  if (hasPlus && cleaned.length >= 10 && cleaned.length <= 15) {
    return `+${cleaned}`;
  }

  return null;
}

/**
 * Format E.164 phone number for human-readable display.
 * US numbers (+1) are formatted as (XXX) XXX-XXXX.
 * Other international numbers show country code with spaces.
 * @param phone - E.164 formatted phone number (e.g., +15551234567)
 * @returns Formatted display string, or original value if not E.164
 */
export function formatE164ForDisplay(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') return '';

  const trimmed = phone.trim();

  // If it doesn't start with +, it's not E.164, return as-is
  if (!trimmed.startsWith('+')) {
    return trimmed;
  }

  // Extract digits after the +
  const digits = trimmed.slice(1).replace(/\D/g, '');

  // US/Canada numbers: +1XXXXXXXXXX -> (XXX) XXX-XXXX
  if (digits.startsWith('1') && digits.length === 11) {
    const areaCode = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const subscriber = digits.slice(7, 11);
    return `(${areaCode}) ${exchange}-${subscriber}`;
  }

  // UK numbers: +44XXXXXXXXXX -> +44 XXX XXXX XXXX
  if (digits.startsWith('44') && digits.length >= 11 && digits.length <= 12) {
    const countryCode = digits.slice(0, 2);
    const rest = digits.slice(2);
    // Format as +44 XXX XXXX XXXX (common UK mobile format)
    if (rest.length === 10) {
      return `+${countryCode} ${rest.slice(0, 3)} ${rest.slice(3, 7)} ${rest.slice(7)}`;
    }
    return `+${countryCode} ${rest}`;
  }

  // Other international: just add spaces every 3-4 digits for readability
  if (digits.length >= 10) {
    const countryCodeLength = digits.length <= 12 ? 2 : 3;
    const countryCode = digits.slice(0, countryCodeLength);
    const rest = digits.slice(countryCodeLength);
    // Split remaining digits into groups
    const groups = rest.match(/.{1,4}/g) || [];
    return `+${countryCode} ${groups.join(' ')}`;
  }

  // Fallback: return original
  return trimmed;
}
